<template>
  <v-container fluid class="pa-4">
    <v-row class="mb-6">
      <v-col cols="12" sm="8">
        <h1 class="text-h4 font-weight-bold primary--text">
          Tipos de Convenios
        </h1>
        <p class="text-subtitle2 grey--text mt-2">
          Gestiona los tipos de convenios disponibles y sus campos asociados
        </p>
      </v-col>
      <v-col cols="12" sm="4" class="text-sm-right">
        <v-btn color="primary" large @click="abrirFormularioNuevo">
          <v-icon left>mdi-plus</v-icon>
          Nuevo Tipo
        </v-btn>
      </v-col>
    </v-row>

    <v-progress-linear v-if="loading" indeterminate></v-progress-linear>

    <!-- Grid de Tipos -->
    <v-row v-if="tiposConvenios.length > 0">
      <v-col
        v-for="tipo in tiposConvenios"
        :key="tipo.id"
        cols="12"
        sm="6"
        md="4"
      >
        <v-card class="elevation-2 h-100">
          <v-card-title class="pb-0">
            <div class="d-flex justify-space-between align-start w-100">
              <div class="text-h6 font-weight-bold">{{ tipo.nombre }}</div>
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    small
                    icon
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>

                <v-list>
                  <v-list-item @click="editarTipo(tipo)">
                    <v-list-item-icon>
                      <v-icon>mdi-pencil</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Editar</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="confirmarEliminar(tipo)" class="error--text">
                    <v-list-item-icon>
                      <v-icon class="error--text">mdi-trash-can</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Eliminar</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text>
            <div class="mb-4">
              <div class="text-caption font-weight-bold grey--text">DESCRIPCIÓN</div>
              <div class="text-body2 mt-1">{{ tipo.descripcion }}</div>
            </div>

            <v-divider class="my-3"></v-divider>

            <div class="mb-4">
              <div class="text-caption font-weight-bold grey--text">DESCUENTO</div>
              <div class="text-h5 primary--text font-weight-bold mt-1">{{ tipo.descuento }}%</div>
            </div>

            <v-divider class="my-3"></v-divider>

            <div class="mb-4">
              <div class="text-caption font-weight-bold grey--text">CAMPOS ASOCIADOS</div>
              <div class="mt-2">
                <div v-if="!tipo.course_agreements_detail || tipo.course_agreements_detail.length === 0" class="text-caption italic grey--text">
                  Sin campos asociados
                </div>
                <v-chip
                  v-for="campo in tipo.course_agreements_detail"
                  :key="campo.id"
                  small
                  class="mr-2 mb-2"
                >
                  {{ campo.name }}
                </v-chip>
              </div>
            </div>

            <v-divider class="my-3"></v-divider>

            <div>
              <div class="text-caption font-weight-bold grey--text">ESTADO</div>
              <v-chip
                :color="tipo.activo ? 'success' : 'error'"
                text-color="white"
                small
                class="mt-1"
              >
                {{ tipo.activo ? 'Activo' : 'Inactivo' }}
              </v-chip>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-else-if="!loading">
      <v-col cols="12">
        <v-alert type="info" outlined>
          No hay tipos de convenios. Haz clic en "Nuevo Tipo" para crear uno.
        </v-alert>
      </v-col>
    </v-row>

    <!-- Dialog de Formulario -->
    <v-dialog v-model="mostrarFormulario" max-width="700px" persistent>
      <v-card>
        <v-card-title class="primary--text">
          {{ modoEdicion ? 'Editar Tipo de Convenio' : 'Nuevo Tipo de Convenio' }}
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <v-form ref="form">
            <v-text-field
              v-model="formulario.nombre"
              label="Nombre"
              outlined
              dense
              required
              class="mb-4"
            ></v-text-field>

            <v-textarea
              v-model="formulario.descripcion"
              label="Descripción"
              outlined
              dense
              rows="3"
              class="mb-4"
            ></v-textarea>

            <v-text-field
              v-model.number="formulario.descuento"
              label="Descuento (%)"
              type="number"
              outlined
              dense
              min="0"
              max="100"
              class="mb-4"
            ></v-text-field>

            <v-select
              v-model="formulario.course_agreements"
              :items="camposDisponibles"
              item-text="name"
              item-value="id"
              label="Campos Asociados"
              outlined
              dense
              multiple
              chips
              small-chips
              deletable-chips
              class="mb-4"
              clearable
              @change="onCamposChange"
            >
              <template v-slot:prepend-item>
                <v-list-item
                  ripple
                  @click="toggleAllCampos"
                >
                  <v-list-item-action>
                    <v-checkbox
                      :input-value="formulario.course_agreements.length === camposDisponibles.length"
                      :indeterminate="formulario.course_agreements.length > 0 && formulario.course_agreements.length < camposDisponibles.length"
                    ></v-checkbox>
                  </v-list-item-action>
                  <v-list-item-title>Seleccionar Todos</v-list-item-title>
                </v-list-item>
                <v-divider class="mt-2"></v-divider>
              </template>
              <template v-slot:item="{ item }">
                <span>{{ item.name }}</span>
                <v-spacer></v-spacer>
                <span class="text-caption grey--text">{{ item.location }}</span>
              </template>
            </v-select>

            <v-checkbox
              v-model="formulario.activo"
              label="Tipo activo"
              class="mb-4"
            ></v-checkbox>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cerrarFormulario">
            Cancelar
          </v-btn>
          <v-btn color="primary" @click="guardarTipo" :loading="guardando">
            {{ modoEdicion ? 'Actualizar' : 'Crear' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de confirmación de eliminación -->
    <v-dialog v-model="mostrarConfirmacion" max-width="400px">
      <v-card>
        <v-card-title class="error--text">
          Confirmar Eliminación
        </v-card-title>
        <v-card-text>
          ¿Estás seguro de que deseas eliminar el tipo "{{ tipoAEliminar?.nombre }}"?
          Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="mostrarConfirmacion = false">
            Cancelar
          </v-btn>
          <v-btn color="error" @click="eliminarTipo">
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" bottom right timeout="4000">
      {{ snackbar.mensaje }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'GolfConveniosTipos',
  data () {
    return {
      mostrarFormulario: false,
      mostrarConfirmacion: false,
      modoEdicion: false,
      tipoEditando: null,
      tipoAEliminar: null,
      guardando: false,
      snackbar: {
        show: false,
        mensaje: '',
        color: 'success'
      },
      formulario: {
        nombre: '',
        descripcion: '',
        descuento: 0,
        activo: true,
        course_agreements: []
      },
      camposDisponibles: []
    }
  },
  computed: {
    ...mapState('golfConvenios', {
      tiposConvenios: 'tipos',
      loading: 'loading',
      error: 'error'
    })
  },
  mounted () {
    this.cargarDatos()
  },
  methods: {
    ...mapActions('golfConvenios', ['fetchTipos', 'createTipo', 'updateTipo', 'deleteTipo']),
    async cargarDatos () {
      try {
        await Promise.all([
          this.fetchTipos(),
          this.cargarCamposDisponibles()
        ])
      } catch (error) {
        this.mostrarSnackbar('Error al cargar datos', 'error')
      }
    },
    async cargarCamposDisponibles () {
      try {
        const { golfConveniosAPI } = await import('@/services/golfConvenios')
        const response = await golfConveniosAPI.camposDisponibles()
        console.log('Response de campos:', response.data)
        this.camposDisponibles = response.data.campos || response.data || []
        console.log('Campos disponibles cargados:', this.camposDisponibles)
      } catch (error) {
        console.error('Error cargando campos disponibles:', error)
        this.mostrarSnackbar('Error al cargar campos disponibles', 'error')
      }
    },
    abrirFormularioNuevo () {
      this.modoEdicion = false
      this.tipoEditando = null
      this.formulario = {
        nombre: '',
        descripcion: '',
        descuento: 0,
        activo: true,
        course_agreements: []
      }
      this.mostrarFormulario = true
    },
    editarTipo (tipo) {
      this.modoEdicion = true
      this.tipoEditando = tipo
      this.formulario = {
        nombre: tipo.nombre,
        descripcion: tipo.descripcion,
        descuento: tipo.descuento,
        activo: tipo.activo,
        course_agreements: tipo.course_agreements || []
      }
      this.mostrarFormulario = true
    },
    cerrarFormulario () {
      this.mostrarFormulario = false
      this.formulario = {
        nombre: '',
        descripcion: '',
        descuento: 0,
        activo: true,
        course_agreements: []
      }
    },
    async guardarTipo () {
      if (!this.$refs.form?.validate?.()) {
        this.mostrarSnackbar('Por favor completa los campos requeridos', 'warning')
        return
      }

      this.guardando = true
      try {
        if (this.modoEdicion) {
          await this.updateTipo({
            id: this.tipoEditando.id,
            data: this.formulario
          })
          this.mostrarSnackbar('✓ Tipo de convenio actualizado', 'success')
        } else {
          await this.createTipo(this.formulario)
          this.mostrarSnackbar('✓ Tipo de convenio creado', 'success')
        }
        await this.cargarDatos()
        this.cerrarFormulario()
      } catch (error) {
        console.error('Error:', error)
        this.mostrarSnackbar(error.message || 'Error al guardar tipo de convenio', 'error')
      } finally {
        this.guardando = false
      }
    },
    confirmarEliminar (tipo) {
      this.tipoAEliminar = tipo
      this.mostrarConfirmacion = true
    },
    async eliminarTipo () {
      try {
        await this.deleteTipo(this.tipoAEliminar.id)
        this.mostrarSnackbar('✓ Tipo de convenio eliminado', 'success')
        await this.cargarDatos()
      } catch (error) {
        this.mostrarSnackbar('Error al eliminar tipo de convenio', 'error')
      } finally {
        this.mostrarConfirmacion = false
        this.tipoAEliminar = null
      }
    },
    toggleAllCampos () {
      if (this.formulario.course_agreements.length === this.camposDisponibles.length) {
        this.formulario.course_agreements = []
      } else {
        this.formulario.course_agreements = this.camposDisponibles.map(c => c.id)
      }
    },
    onCamposChange () {
      console.log('Campos seleccionados:', this.formulario.course_agreements)
    },
    mostrarSnackbar (mensaje, color) {
      this.snackbar = {
        show: true,
        mensaje,
        color
      }
    }
  }
}
</script>
